import React from 'react';
import BaseCard from '../../../components/misc/BaseCard.jsx';
import DashboardMetricsCarousel from '../../../components/misc/DashboardMetricsCarousel.jsx';
import { formatNumbers } from '../../../utils/formatNumbers.js';
import ThreeDotsLoader from '../ThreeDotsLoader.jsx';

const StatsPerMap = ({ data, titles = {} }) => {
    if (!data) {
        return <ThreeDotsLoader className='mt-6' />;
    }

    const renderNumericCards = (numericData) => {
        const gridClasses = numericData.length === 1 ? 'grid grid-cols-1' : 'grid sm:grid-cols-2 sm:gap-4';
        return (
            <div className={`${gridClasses} w-full`}>
                {numericData.map(({ label, value }, index) => (
                    <BaseCard key={index} className='p-4 py-8 rounded-4xl shadow-mildSubtle text-center'>
                        <div className='font-bold text-theme1-gold text-[40px] leading-none'>
                            {formatNumbers(Number(value))}
                        </div>
                        <div className='font-bold text-theme1-title2 mt-1'>{label}</div>
                    </BaseCard>
                ))}
            </div>
        );
    };

    const renderArrayCarousel = (title, items) => {
        const total = items.reduce((acc, item) => acc + (item.count || 0), 0);
        return (
            <div className='mt-4'>
                <div className='text-theme1-title2 font-bold text-[22px] mb-2'>{title}</div>
                <DashboardMetricsCarousel items={items} total={total} displayMode='absolute' />
            </div>
        );
    };

    const categoryMappings = {
        contacts: {
            numeric: [
                { label: 'Total Contacts', value: data.contacts.total_contacts },
                { label: 'Total Companies', value: data.contacts.total_companies }
            ],
            arrays: [
                { title: 'Contacts Per Company', key: 'contacts_per_company' },
                { title: 'Contacts Per Country', key: 'contacts_per_country' },
                { title: 'Contacts Per Affiliation', key: 'contacts_per_affiliation' },
                { title: 'Skills Distribution', key: 'skills_distribution' },
                { title: 'Job Titles Distribution', key: 'job_titles_distribution' }
            ]
        },
        organizations: {
            numeric: [
                { label: 'Total Organizations', value: data.organizations.total_organizations },
                { label: 'Total Revenue', value: data.organizations.total_revenue },
                { label: 'Total Employees', value: data.organizations.total_employees },
                { label: 'Average Profit', value: data.organizations.average_profit },
                { label: 'Total Costs', value: data.organizations.total_costs }
            ],
            arrays: [
                { title: 'Industry Distribution', key: 'industries_distribution' },
                { title: 'Ownership Distribution', key: 'ownership_distribution' },
                { title: 'Geographical Distribution', key: 'geographical_distribution' },
                { title: 'Needs Distribution', key: 'needs_distribution' },
                { title: 'Capabilities Distribution', key: 'capabilities_distribution' }
            ]
        },
        projects: {
            numeric: [
                { label: 'Total Projects', value: data.projects.total_projects },
                { label: 'Total Milestones', value: data.projects.total_milestones },
                { label: 'Total Reports', value: data.projects.total_reports }
            ],
            arrays: [
                { title: 'Status Distribution', key: 'status_distribution' },
                { title: 'Companies Distribution', key: 'companies_distribution' },
                { title: 'Needs Distribution', key: 'needs_distribution' },
                { title: 'Capabilities Distribution', key: 'capabilities_distribution' },
                { title: 'Milestones Over Time', key: 'milestones_over_time' },
                { title: 'Reports Over Time', key: 'reports_over_time' },
                { title: 'Reach Distribution', key: 'reach_distribution' }
            ]
        },
        files: {
            numeric: [{ label: 'Total Files', value: data.files.total_files }],
            arrays: [
                { title: 'Country Distribution', key: 'country_distribution' },
                { title: 'Category Distribution', key: 'category_distribution' },
                { title: 'Extension Distribution', key: 'extension_distribution' }
            ]
        },
        calendar: {
            numeric: [{ label: 'Total Calendar Meetings', value: data.calendar.total_calendar_meetings }],
            arrays: [
                { title: 'Country Distribution', key: 'country_distribution' },
                { title: 'Files Distribution', key: 'files_distribution' },
                { title: 'Objectives Distribution', key: 'objectives_distribution' },
                { title: 'Organizations Distribution', key: 'organizations_distribution' },
                { title: 'Participants Distribution', key: 'participants_distribution' },
                { title: 'Projects Distribution', key: 'projects_distribution' },
                { title: 'Follow Up Distribution', key: 'follow_up_distribution' },
                { title: 'All Day Distribution', key: 'all_day_distribution' },
                { title: 'Start Dates Distribution', key: 'start_dates_distribution' },
                { title: 'End Dates Distribution', key: 'end_dates_distribution' }
            ]
        }
    };

    const renderCategorySection = (categoryKey) => {
        const categoryData = categoryMappings[categoryKey];
        if (!categoryData) return null;

        const customTitle = titles[categoryKey] || categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1); // default title if none provided

        return (
            <section className='mb-8' key={categoryKey}>
                <h2 className='text-2xl font-bold mb-1 pb-2 border-b border-theme1-text1'>{customTitle}</h2>

                {categoryData.numeric && categoryData.numeric.length > 0 && renderNumericCards(categoryData.numeric)}

                {categoryData.arrays &&
                    categoryData.arrays.map((arr) => {
                        const items = data[categoryKey][arr.key];
                        return items && items.length > 0 ? (
                            <React.Fragment key={arr.key}>{renderArrayCarousel(arr.title, items)}</React.Fragment>
                        ) : null;
                    })}
            </section>
        );
    };

    return (
        <div className='container mx-auto px-4 py-6'>
            {Object.keys(categoryMappings).map((categoryKey) => renderCategorySection(categoryKey))}
        </div>
    );
};

export default StatsPerMap;
